import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { isAdminEmail } from '../../utils/admin';
import { DashSpinner } from './dash-loading';

/**
 * Admins only, for every /dash page, checked in the browser.
 *
 * Every dash page used to export `getServerSideProps = requireAdmin`, which put
 * a server round trip -- often a cold function start -- in front of every rail
 * click, for a check that returned empty props. That was most of the stall the
 * owner saw (2026-10-26: "right now it just stalls until everything loads").
 * The pages are static now and this gate holds the line instead: the page
 * component does not mount, so none of its requests go out, until the session
 * says admin. A non-admin never sees dash markup.
 *
 * Nothing secret was ever in the page itself. Every API the dash reads checks
 * admin on its own, and that is what protects the data.
 */

/** Whether a route is a dash page. */
export function isDashPath(pathname: string): boolean {
  return pathname === '/dash' || pathname.startsWith('/dash/');
}

export default function DashGate({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const { data: session, status } = useSession();
  const email = session?.user?.email ?? null;
  const admin = status === 'authenticated' && isAdminEmail(email);

  useEffect(() => {
    if (status === 'loading' || admin || !router.isReady) return;
    if (status === 'unauthenticated') {
      void router.replace(`/login?returnTo=${encodeURIComponent(router.asPath)}`);
      return;
    }
    // Signed in, not an admin: home, as requireAdmin did. Login would bounce
    // them straight back here.
    void router.replace('/');
  }, [status, admin, router, router.isReady, router.asPath]);

  if (admin) return <>{children}</>;
  return (
    <div className="dash-scope flex min-h-screen items-start justify-center pt-40" data-testid="dash-gate">
      <DashSpinner label={status === 'loading' ? 'Loading the dash' : 'Redirecting'} />
    </div>
  );
}
